import React, { useEffect, useState, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import BranchLoyaltyAnalytics, { useCurrentBranch } from './BranchLoyaltyAnalytics'
import { authHeaders } from '../../utils/authFetch'

import { useNetworkStatus } from '../../offline/useNetworkStatus'
import { offlineDB } from '../../offline/db'
import {
    cacheLoyaltyCatalog,
    getLoyaltyTiersOffline,
    getLoyaltyCatalogRewardsOffline,
    getLoyaltyCatalogAchievementsOffline,
    getLoyaltyCatalogCampaignsOffline,
} from '../../offline/posOfflineHelpers'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)


export default function LoyaltyRewardsStaff() {
    const [rewards, setRewards] = useState([])
    const [tiers, setTiers] = useState([])
    const [tierPage, setTierPage] = useState(1)
    const [tierSearch, setTierSearch] = useState('')
    const [campaigns, setCampaigns] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('achievements')
    const isOnline = useNetworkStatus()

    // Current admin's assigned branch 
    const { branchId, branchName, canViewAnalytics } = useCurrentBranch()
    useEffect(() => {
        if (canViewAnalytics) setActiveTab('analytics')
    }, [canViewAnalytics])

    // Reward filters
    const [rewardLogicFilter, setRewardLogicFilter] = useState('all')
    const [rewardDurationFilter, setRewardDurationFilter] = useState('all')
    const [rewardPage, setRewardPage] = useState(1)

    // Campaign filters
    const [campaignTypeFilter, setCampaignTypeFilter] = useState('all')
    const [campaignStatusFilter, setCampaignStatusFilter] = useState('all')
    const [campaignPage, setCampaignPage] = useState(1)
    const PAGE_SIZE = 10
    const TIER_PAGE_SIZE = 5

    // Search
    const [rewardSearch, setRewardSearch] = useState('')
    const [campaignSearch, setCampaignSearch] = useState('')
    const [achievementSearch, setAchievementSearch] = useState('')

    // Achievements
    const [achievements, setAchievements] = useState([])
    const [achievementPage, setAchievementPage] = useState(1)

    // Detail viewer modal
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [viewingType, setViewingType] = useState(null) // 'reward' | 'campaign' | 'achievement'
    const [viewingItem, setViewingItem] = useState(null)
    const [viewDetails, setViewDetails] = useState({})
    const [viewLoading, setViewLoading] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            await Promise.all([fetchRewards(), fetchCampaigns(), fetchAchievements(), fetchTiers()])
            setLoading(false)
        }
        fetchData()
    }, [isOnline])

    useEffect(() => {
        const handleSyncComplete = () => {
            fetchRewards(); fetchCampaigns(); fetchAchievements(); fetchTiers()
        }
        window.addEventListener('kaperk:sync-complete', handleSyncComplete)
        return () => window.removeEventListener('kaperk:sync-complete', handleSyncComplete)
    }, [])

    useEffect(() => { setTierPage(1) }, [tierSearch])
    useEffect(() => { setRewardPage(1) }, [rewardLogicFilter, rewardDurationFilter, rewardSearch])
    useEffect(() => { setCampaignPage(1) }, [campaignTypeFilter, campaignStatusFilter, campaignSearch])
    useEffect(() => { setAchievementPage(1) }, [achievementSearch])

    const fetchRewards = async () => {
        let data
        if (!isOnline) {
            data = await getLoyaltyCatalogRewardsOffline()
        } else {
            const { data: onlineData, error } = await supabase
                .schema('marketing')
                .from('rewards')
                .select('*')
                .eq('is_archived', false)
                .eq('is_active', true)
                .order('created_at', { ascending: false })
            if (error) { console.error(error); return }
            data = onlineData
        }

        const today = new Date()
        const active = (data || []).filter(r =>
            r.availability_status !== 'limited' ||
            !r.end_at ||
            new Date(r.end_at) >= today
        )
        setRewards(active.map(r => ({
            id: r.reward_id,
            name: r.reward_name,
            category: r.application_scope === 'category'
                ? r.applies_to_category
                : r.application_scope === 'specific_items'
                    ? 'Specific Item(s)'
                    : 'All Items',
            applicationScope: r.application_scope || 'all',
            appliesToCategory: r.applies_to_category || '',
            description: r.description || '',
            mechanics: r.mechanics || '',
            pointsRequired: r.points_required,
            rewardLogicType: r.reward_logic_type || 'point',
            rewardValueType: r.reward_value_type || '',
            rewardValue: r.reward_value ?? '',
            minimumSpend: r.minimum_spend ?? '',
            maxDiscountAmount: r.max_discount_amount ?? '',
            promoMinOrderCount: r.promo_min_order_count ?? '',
            durationType: r.availability_status === 'limited' ? 'Limited' : 'Permanent',
            startDate: r.start_at ? r.start_at.split('T')[0] : '',
            endDate: r.end_at ? r.end_at.split('T')[0] : '',
        })))
    }

    const fetchCampaigns = async () => {
        try {
            let data
            if (!isOnline) {
                data = await getLoyaltyCatalogCampaignsOffline()
            } else {
                const res = await fetch(`${BASE_URL}/api/loyalty-rewards/campaigns`, { headers: authHeaders() })
                if (!res.ok) throw new Error('Failed to load campaigns')
                const parsed = await res.json()
                data = parsed.campaigns
            }

            const now = new Date()
            const active = (data || []).filter(c =>
                c.status !== 'cancelled' &&
                new Date(c.end_at) >= now
            )

            setCampaigns(active.map(c => ({
                id: c.promotion_id,
                name: c.promotion_name,
                type: formatType(c.promotion_type),
                rawType: c.promotion_type,
                status: c.status,
                reward: c.reward_description || '',
                description: c.description || '',
                mechanics: c.mechanics || '',
                startDate: c.start_at ? c.start_at.split('T')[0] : '',
                endDate: c.end_at ? c.end_at.split('T')[0] : '',
                isLive: new Date(c.start_at) <= now && new Date(c.end_at) >= now,
                linkedRewardId: c.linked_reward_id || '',
                promotionMechanicType: c.promotion_mechanic_type || '',
                multiplier: c.multiplier ?? '',
                discountValue: c.discount_value ?? '',
                discountValueType: c.discount_value_type || '',
                minimumSpend: c.minimum_spend ?? '',
                minOrderCount: c.min_order_count ?? '',
            })))
        } catch (err) {
            console.error('Error fetching campaigns:', err)
        }
    }

    const fetchTiers = async () => {
        if (!isOnline) {
            const cached = await getLoyaltyTiersOffline()
            setTiers(cached)
            return
        }
        const { data, error } = await supabase
            .schema('marketing')
            .from('loyalty_tiers')
            .select(`tier_id, tier_name, tier_level, points_required, tier_icon,
                loyalty_tier_benefits ( benefit_description )`)
            .eq('is_archived', false)
            .order('tier_level', { ascending: true })
        if (error) { console.error(error); return }

        setTiers((data || []).map((tier, index, arr) => ({
            id: tier.tier_id,
            name: tier.tier_name,
            tierLevel: tier.tier_level,
            minPoints: tier.points_required,
            maxPoints: index < arr.length - 1 ? arr[index + 1].points_required - 1 : null,
            benefits: (tier.loyalty_tier_benefits || []).map(b => b.benefit_description),
            image_url: tier.tier_icon || '',
        })))

        // refresh cache so the next offline session has fresh data
        cacheLoyaltyCatalog().catch(err => console.error('Loyalty catalog cache refresh failed:', err))
    }

    const fetchAchievements = async () => {
        let data
        if (!isOnline) {
            data = await getLoyaltyCatalogAchievementsOffline()
        } else {
            const { data: onlineData, error } = await supabase
                .schema('marketing')
                .from('achievements')
                .select(`
                    achievement_id, title, description, reward_points, badge_icon, status, is_repeatable,
                    achievement_rules ( rule_id, condition_label, rule_type, target_value, timeframe_type )
                `)
                .eq('is_archived', false)
                .eq('status', 'active')
                .order('created_at', { ascending: false })
            if (error) { console.error(error); return }
            data = onlineData
        }

        setAchievements((data || []).map(a => ({
            id: a.achievement_id,
            title: a.title,
            description: a.description || '',
            rewardPoints: a.reward_points || 0,
            badgeIcon: a.badge_icon || '',
            isRepeatable: a.is_repeatable,
            condition: a.achievement_rules?.[0]?.condition_label || '',
        })))
    }

    const formatType = (dbType) => {
        const map = {
            points_multiplier: 'Points Multiplier',
            challenge: 'Challenge',
            time_based_reward: 'Time-Based Reward',
            discount: 'Discount',
            bundle: 'Bundle',
            bonus_points: 'Bonus Points',
        }
        return map[dbType] ?? dbType
    }

    const logicTypeLabel = (type) => {
        const map = {
            point: 'Points Redemption',
            order: 'Order-Based',
            achievement: 'Achievement Unlock',
            promo: 'Promo / Campaign',
        }
        return map[type] ?? type
    }

    // Derived campaign type options from live data
    const campaignTypes = useMemo(() => {
        const unique = [...new Set(campaigns.map(c => c.rawType).filter(Boolean))]
        return unique.sort()
    }, [campaigns])

    // Filtered lists
    const filteredRewards = useMemo(() => {
        const query = rewardSearch.trim().toLowerCase()
        return rewards.filter(r => {
            const matchLogic = rewardLogicFilter === 'all' || r.rewardLogicType === rewardLogicFilter
            const matchDuration = rewardDurationFilter === 'all' || r.durationType === rewardDurationFilter
            const matchSearch = !query ||
                r.name.toLowerCase().includes(query) ||
                r.description.toLowerCase().includes(query) ||
                r.mechanics.toLowerCase().includes(query)
            return matchLogic && matchDuration && matchSearch
        })
    }, [rewards, rewardLogicFilter, rewardDurationFilter, rewardSearch])

    const filteredCampaigns = useMemo(() => {
        const query = campaignSearch.trim().toLowerCase()
        return campaigns.filter(c => {
            const matchType = campaignTypeFilter === 'all' || c.rawType === campaignTypeFilter
            const matchStatus = campaignStatusFilter === 'all'
                || (campaignStatusFilter === 'live' && c.isLive)
                || (campaignStatusFilter === 'upcoming' && !c.isLive)
            const matchSearch = !query ||
                c.name.toLowerCase().includes(query) ||
                c.description.toLowerCase().includes(query) ||
                c.mechanics.toLowerCase().includes(query)
            return matchType && matchStatus && matchSearch
        })
    }, [campaigns, campaignTypeFilter, campaignStatusFilter, campaignSearch])

    const filteredAchievements = useMemo(() => {
        const query = achievementSearch.trim().toLowerCase()
        if (!query) return achievements
        return achievements.filter(a =>
            a.title.toLowerCase().includes(query) ||
            a.description.toLowerCase().includes(query) ||
            a.condition.toLowerCase().includes(query)
        )
    }, [achievements, achievementSearch])

    // Paginated slices
    const pagedRewards = filteredRewards.slice((rewardPage - 1) * PAGE_SIZE, rewardPage * PAGE_SIZE)
    const pagedCampaigns = filteredCampaigns.slice((campaignPage - 1) * PAGE_SIZE, campaignPage * PAGE_SIZE)
    const pagedAchievements = filteredAchievements.slice((achievementPage - 1) * PAGE_SIZE, achievementPage * PAGE_SIZE)

    const activeRewardFilters = rewardLogicFilter !== 'all' || rewardDurationFilter !== 'all'
    const activeCampaignFilters = campaignTypeFilter !== 'all' || campaignStatusFilter !== 'all'

    const openDetailModal = async (type, item) => {
        setViewingType(type)
        setViewingItem(item)
        setViewDetails({})
        setShowDetailModal(true)
        setViewLoading(true)

        try {
            if (type === 'campaign') {
                const details = {}

                const { data: banner } = await supabase
                    .schema('marketing')
                    .from('hero_banners')
                    .select('banner_id, title, subtitle, image_url')
                    .eq('linked_promotion_id', item.id)
                    .eq('is_archived', false)
                    .maybeSingle()
                if (banner) details.heroBanner = banner

                if (item.linkedRewardId) {
                    const { data: reward } = await supabase
                        .schema('marketing')
                        .from('rewards')
                        .select('reward_id, reward_name, description, reward_value, reward_value_type, points_required')
                        .eq('reward_id', item.linkedRewardId)
                        .maybeSingle()
                    if (reward) details.linkedReward = reward
                }

                setViewDetails(details)
            }

            if (type === 'reward') {
                const details = {}

                if (item.applicationScope === 'specific_items') {
                    const { data: linkRows, error: itemsError } = await supabase
                        .schema('marketing')
                        .from('reward_items')
                        .select('item_id, variant_id')
                        .eq('reward_id', item.id)

                    if (itemsError) console.error('Error fetching reward_items:', itemsError)

                    const linkedRows = linkRows || []
                    const itemIds = [...new Set(linkedRows.map(r => r.item_id).filter(Boolean))]
                    const variantIds = [...new Set(linkedRows.map(r => r.variant_id).filter(Boolean))]

                    let menuItemsById = {}
                    let variantsById = {}

                    if (itemIds.length > 0) {
                        const { data: menuItemRows, error: menuItemsError } = await supabase
                            .from('menu_items')
                            .select('item_id, name, item_price, item_img, category')
                            .in('item_id', itemIds)

                        if (menuItemsError) console.error('Error fetching menu_items:', menuItemsError)
                        menuItemsById = Object.fromEntries((menuItemRows || []).map(m => [m.item_id, m]))
                    }

                    if (variantIds.length > 0) {
                        const { data: variantRows, error: variantsError } = await supabase
                            .from('item_variants')
                            .select('variant_id, variant_name, price')
                            .in('variant_id', variantIds)

                        if (variantsError) console.error('Error fetching item_variants:', variantsError)
                        variantsById = Object.fromEntries((variantRows || []).map(v => [v.variant_id, v]))
                    }

                    details.linkedItems = linkedRows.map(row => ({
                        menuItem: menuItemsById[row.item_id] || null,
                        variant: row.variant_id ? variantsById[row.variant_id] || null : null,
                    }))
                } else if (item.applicationScope === 'category') {
                    details.linkedCategory = item.appliesToCategory
                }

                // Order-based rewards store their thresholds in reward_rules, not on the reward row itself
                if (item.rewardLogicType === 'order') {
                    const { data: rules, error: rulesError } = await supabase
                        .schema('marketing')
                        .from('reward_rules')
                        .select('rule_type, target_value')
                        .eq('reward_id', item.id)

                    if (rulesError) console.error('Error fetching reward rules:', rulesError)
                    details.orderCountRule = (rules || []).find(r => r.rule_type === 'completed_orders')
                    details.minSpendRule = (rules || []).find(r => r.rule_type === 'minimum_spend')
                }

                // Achievement-gated rewards: show which achievement unlocks them
                if (item.rewardLogicType === 'achievement') {
                    const { data: rule, error: achRuleError } = await supabase
                        .schema('marketing')
                        .from('reward_rules')
                        .select('achievement_id')
                        .eq('reward_id', item.id)
                        .eq('rule_type', 'achievement_required')
                        .maybeSingle()

                    if (achRuleError) console.error('Error fetching achievement rule:', achRuleError)

                    if (rule?.achievement_id) {
                        const { data: ach } = await supabase
                            .schema('marketing')
                            .from('achievements')
                            .select('title, reward_points')
                            .eq('achievement_id', rule.achievement_id)
                            .maybeSingle()
                        if (ach) details.requiredAchievement = ach
                    }
                }

                setViewDetails(details)
            }

            if (type === 'achievement') {
                const { data: rule } = await supabase
                    .schema('marketing')
                    .from('reward_rules')
                    .select('reward_id')
                    .eq('rule_type', 'achievement_required')
                    .eq('achievement_id', item.id)
                    .maybeSingle()

                if (rule?.reward_id) {
                    const { data: reward } = await supabase
                        .schema('marketing')
                        .from('rewards')
                        .select('reward_id, reward_name, description, reward_value, reward_value_type, points_required')
                        .eq('reward_id', rule.reward_id)
                        .maybeSingle()
                    if (reward) setViewDetails({ linkedReward: reward })
                }
            }
        } catch (err) {
            console.error('Error loading detail view:', err)
        } finally {
            setViewLoading(false)
        }
    }

    const closeDetailModal = () => {
        setShowDetailModal(false)
        setViewingType(null)
        setViewingItem(null)
        setViewDetails({})
    }

    // ─── Pagination  ─────────────────────────────────────────────
    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalItems === 0) return null

        return (
            <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
            >
                <span style={{ fontSize: 14, color: C.midgrey }}>
                    Showing <span className="font-medium text-gray-700">{Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium text-gray-700">{totalItems}</span> items
                </span>
                {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === 1 ? C.border : C.textSoft,
                            fontSize: 16, cursor: page === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                        .reduce((acc, n, idx, arr) => {
                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                            acc.push(n)
                            return acc
                        }, [])
                        .map((n, idx) =>
                            n === "…" ? (
                                <span key={`e-${idx}`} style={{ fontSize: 14, color: C.midgrey, padding: "0 2px" }}>…</span>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        border: `1px solid ${n === page ? C.nude : C.border}`,
                                        background: n === page ? C.nude : C.card,
                                        color: n === page ? "#fff" : C.textSoft,
                                        fontSize: 14, fontWeight: n === page ? 600 : 400,
                                        cursor: "pointer",
                                    }}>
                                    {n}
                                </button>
                            )
                        )
                    }
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === totalPages ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === totalPages ? "not-allowed" : "pointer",
                        }}>
                       Next →
                    </button>
            </div>
            )}
        </div>
        )
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="px-4 sm:px-8 pt-6">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {[
                        ...(canViewAnalytics ? [{ key: 'analytics', label: 'Loyalty Analytics' }] : []),
                        { key: 'tiers', label: 'Customer Tiers' },
                        { key: 'achievements', label: 'Achievements' },
                        { key: 'rewards', label: 'Active Rewards' },
                        { key: 'campaigns', label: 'Active Campaigns' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-5 py-2.5 rounded-lg font-medium transition-colors ${activeTab === tab.key
                                    ? 'bg-[#8B5A3C] text-white'
                                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-8">

                {/* ── Loyalty Analytics Tab ── */}
                {activeTab === 'analytics' && canViewAnalytics && (
                    <BranchLoyaltyAnalytics branchId={branchId} branchName={branchName} canViewAnalytics={canViewAnalytics} />
                )}

                {/* ── Tiers Tab ── */}
                {activeTab === 'tiers' && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Customer Tiers</h2>
                                    <p className="text-sm text-gray-500 mt-1">{tiers.length} active tier{tiers.length !== 1 ? 's' : ''}</p>
                                </div>
                                <div className="relative w-full sm:w-56">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        value={tierSearch}
                                        onChange={(e) => setTierSearch(e.target.value)}
                                        placeholder="Search tiers..."
                                        className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {tiers
                                .filter(t => !tierSearch || t.name.toLowerCase().includes(tierSearch.toLowerCase()))
                                .slice((tierPage - 1) * TIER_PAGE_SIZE, tierPage * TIER_PAGE_SIZE)
                                .map(tier => (                                    <div key={tier.id} className="px-4 sm:px-6 py-5 flex items-start gap-4">
                                        {tier.image_url && (
                                            <img src={tier.image_url} alt={tier.name} className="w-12 h-12 object-cover rounded-full flex-shrink-0" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-gray-800 text-base break-words">{tier.name}</p>
                                            <p className="text-sm text-gray-600 mt-1 break-words">
                                                {tier.maxPoints !== null ? `${tier.minPoints} - ${tier.maxPoints} pts` : `${tier.minPoints}+ pts`}
                                            </p>
                                            <div className="mt-2 space-y-1">
                                                {tier.benefits.map((b, i) => <p key={i} className="text-sm text-gray-600 break-words">• {b}</p>)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                        <Pagination
                            page={tierPage}
                            totalItems={tiers.filter(t => !tierSearch || t.name.toLowerCase().includes(tierSearch.toLowerCase())).length}
                            pageSize={TIER_PAGE_SIZE}
                            onPageChange={setTierPage}
                        />
                    </div>
                )}

                {/* ── Achievements Tab ── */}
                {activeTab === 'achievements' && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Achievements</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {filteredAchievements.length} of {achievements.length} achievement{achievements.length !== 1 ? 's' : ''} shown
                                    </p>
                                </div>
                                <div className="relative w-full sm:w-56">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        value={achievementSearch}
                                        onChange={(e) => setAchievementSearch(e.target.value)}
                                        placeholder="Search achievements..."
                                        className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {loading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="px-6 py-5">
                                        <SkeletonBlock className="h-4 w-48 mb-2" />
                                        <SkeletonBlock className="h-3 w-full" />
                                    </div>
                                ))
                            ) : filteredAchievements.length === 0 ? (
                                <div className="px-6 py-12 text-center text-gray-500">
                                    No achievements found.
                                </div>
                            ) : pagedAchievements.map(achievement => (
                                <div
                                    key={achievement.id}
                                    className="px-6 py-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => openDetailModal('achievement', achievement)}
                                >
                                    <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-2 sm:gap-4">
                                        <div className="flex items-start gap-4 flex-1 min-w-0">
                                            {achievement.badgeIcon && (
                                                <img
                                                    src={achievement.badgeIcon}
                                                    alt={achievement.title}
                                                    className="w-12 h-12 object-cover rounded-full flex-shrink-0"
                                                />
                                            )}
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-800 text-base break-words">{achievement.title}</p>
                                                <p className="text-sm text-gray-600 mt-1 break-words">{achievement.description}</p>
                                                {achievement.condition && (
                                                    <p className="text-xs text-gray-500 mt-1 break-words">Condition: {achievement.condition}</p>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-lg font-bold text-[#8B5A3C] flex-shrink-0">
                                            {achievement.rewardPoints} pts
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            page={achievementPage}
                            totalItems={filteredAchievements.length}
                            pageSize={PAGE_SIZE}
                            onPageChange={setAchievementPage}
                        />
                    </div>
                )}

                {/* ── Rewards Tab ── */}
                {activeTab === 'rewards' && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Active Rewards</h2>
                                    {loading ? (
                                        <SkeletonBlock className="h-4 w-40 mt-1" />
                                    ) : (
                                        <p className="text-sm text-gray-500 mt-1">
                                            {filteredRewards.length} of {rewards.length} reward{rewards.length !== 1 ? 's' : ''} shown
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                    <div className="relative flex-1 min-w-[160px] sm:flex-none">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            value={rewardSearch}
                                            onChange={(e) => setRewardSearch(e.target.value)}
                                            placeholder="Search rewards..."
                                            className="pl-9 pr-4 py-2 w-full sm:w-48 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] focus:border-transparent"
                                        />
                                    </div>
                                    {/* Reward logic type filter */}
                                    <select
                                        value={rewardLogicFilter}
                                        onChange={(e) => setRewardLogicFilter(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] bg-white"
                                    >
                                        <option value="all">All Reward Types</option>
                                        <option value="point">Points Redemption</option>
                                        <option value="order">Order-Based</option>
                                        <option value="achievement">Achievement Unlock</option>
                                        <option value="promo">Promo / Campaign</option>
                                    </select>

                                    {/* Availability filter */}
                                    <select
                                        value={rewardDurationFilter}
                                        onChange={(e) => setRewardDurationFilter(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] bg-white"
                                    >
                                        <option value="all">All Availability</option>
                                        <option value="Permanent">Permanent</option>
                                        <option value="Limited">Limited-Time</option>
                                    </select>

                                    {activeRewardFilters && (
                                        <button
                                            onClick={() => {
                                                setRewardLogicFilter('all')
                                                setRewardDurationFilter('all')
                                            }}
                                            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className="px-4 sm:px-6 py-5">
                                        <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 sm:gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <SkeletonBlock className="h-4 w-48" />
                                                    <SkeletonBlock className="h-4 w-20 rounded-full" />
                                                    <SkeletonBlock className="h-4 w-28 rounded-full" />
                                                </div>
                                                <SkeletonBlock className="h-3 w-full mb-1.5" />
                                                <SkeletonBlock className="h-3 w-2/3" />
                                            </div>
                                            <div className="text-left sm:text-right flex-shrink-0 sm:min-w-[120px] w-full sm:w-auto space-y-2">
                                                <SkeletonBlock className="h-5 w-16 sm:ml-auto" />
                                                <SkeletonBlock className="h-4 w-20 sm:ml-auto" />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : filteredRewards.length === 0 ? (
                                <div className="px-6 py-12 text-center text-gray-500">
                                    {activeRewardFilters
                                        ? 'No rewards match the selected filters.'
                                        : 'No active rewards at the moment.'}
                                </div>
                            ) : pagedRewards.map(reward => (
                                <div
                                    key={reward.id}
                                    className="px-6 py-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => openDetailModal('reward', reward)}
                                >
                                    <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 sm:gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <p className="font-semibold text-gray-800 text-base break-words">{reward.name}</p>
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F3EAE4] text-[#8B5A3C]">
                                                    {reward.category}
                                                </span>
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                                    {logicTypeLabel(reward.rewardLogicType)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 mb-2">{reward.description}</p>

                                            {reward.mechanics && (
                                                <div className="bg-gray-50 rounded-lg px-4 py-3 mt-2">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mechanics</p>
                                                    <p className="text-sm text-gray-700 whitespace-pre-line break-words">{reward.mechanics}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-left sm:text-right flex-shrink-0 sm:min-w-[120px] w-full sm:w-auto">
                                            {reward.rewardLogicType === 'point' && (
                                                <p className="text-lg font-bold text-[#8B5A3C]">
                                                    {reward.pointsRequired != null ? `${reward.pointsRequired} pts` : '—'}
                                                </p>
                                            )}
                                            {reward.rewardValue !== '' && reward.rewardValue != null && (
                                                <p className="text-sm font-semibold text-gray-700 mt-1">
                                                    {reward.rewardValueType === 'percentage'
                                                        ? `${reward.rewardValue}% off`
                                                        : `₱${Number(reward.rewardValue).toLocaleString()} off`}
                                                </p>
                                            )}
                                            {reward.minimumSpend !== '' && reward.minimumSpend != null && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Min. spend: ₱{Number(reward.minimumSpend).toLocaleString()}
                                                </p>
                                            )}
                                            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${reward.durationType === 'Limited'
                                                    ? 'bg-amber-100 text-amber-700'
                                                    : 'bg-green-100 text-green-700'
                                                }`}>
                                                {reward.durationType}
                                            </span>
                                            {reward.durationType === 'Limited' && reward.endDate && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Until {reward.endDate}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            page={rewardPage}
                            totalItems={filteredRewards.length}
                            pageSize={PAGE_SIZE}
                            onPageChange={setRewardPage}
                        />
                    </div>
                )}

                {/* ── Campaigns Tab ── */}
                {activeTab === 'campaigns' && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Active Campaigns</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {filteredCampaigns.length} of {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} shown
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                    <div className="relative flex-1 min-w-[160px] sm:flex-none">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            value={campaignSearch}
                                            onChange={(e) => setCampaignSearch(e.target.value)}
                                            placeholder="Search campaigns..."
                                            className="pl-9 pr-4 py-2 w-full sm:w-48 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] focus:border-transparent"
                                        />
                                    </div>
                                    {/* Campaign type filter — populated from live data */}
                                    <select
                                        value={campaignTypeFilter}
                                        onChange={(e) => setCampaignTypeFilter(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] bg-white"
                                    >
                                        <option value="all">All Types</option>
                                        {campaignTypes.map(type => (
                                            <option key={type} value={type}>
                                                {formatType(type)}
                                            </option>
                                        ))}
                                    </select>

                                    {/* Live vs Upcoming filter */}
                                    <select
                                        value={campaignStatusFilter}
                                        onChange={(e) => setCampaignStatusFilter(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] bg-white"
                                    >
                                        <option value="all">All Statuses</option>
                                        <option value="live">Live Now</option>
                                        <option value="upcoming">Upcoming</option>
                                    </select>

                                    {activeCampaignFilters && (
                                        <button
                                            onClick={() => {
                                                setCampaignTypeFilter('all')
                                                setCampaignStatusFilter('all')
                                            }}
                                            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className="px-4 sm:px-6 py-5">
                                        <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 sm:gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <SkeletonBlock className="h-4 w-48" />
                                                    <SkeletonBlock className="h-4 w-24 rounded-full" />
                                                    <SkeletonBlock className="h-4 w-20 rounded-full" />
                                                </div>
                                                <SkeletonBlock className="h-3 w-1/3 mb-1.5" />
                                                <SkeletonBlock className="h-3 w-full" />
                                            </div>
                                            <div className="text-left sm:text-right flex-shrink-0 sm:min-w-[130px] w-full sm:w-auto space-y-2">
                                                <SkeletonBlock className="h-3 w-14 sm:ml-auto" />
                                                <SkeletonBlock className="h-4 w-28 sm:ml-auto" />
                                                <SkeletonBlock className="h-4 w-28 sm:ml-auto" />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : filteredCampaigns.length === 0 ? (
                                <div className="px-6 py-12 text-center text-gray-500">
                                    {activeCampaignFilters
                                        ? 'No campaigns match the selected filters.'
                                        : 'No active campaigns at the moment.'}
                                </div>
                            ) : pagedCampaigns.map(campaign => (
                                <div
                                    key={campaign.id}
                                    className="px-6 py-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => openDetailModal('campaign', campaign)}
                                >
                                    <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 sm:gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <p className="font-semibold text-gray-800 text-base">{campaign.name}</p>
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                    {campaign.type}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${campaign.isLive
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {campaign.isLive ? 'Live Now' : 'Upcoming'}
                                                </span>
                                            </div>
                                            <p className="text-sm font-medium text-[#8B5A3C] mb-1 break-words">{campaign.reward}</p>
                                            <p className="text-sm text-gray-600 mb-2 break-words">{campaign.description}</p>

                                            {campaign.mechanics && (
                                                <div className="bg-gray-50 rounded-lg px-4 py-3 mt-2">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mechanics</p>
                                                    <p className="text-sm text-gray-700 whitespace-pre-line break-words">{campaign.mechanics}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-left sm:text-right flex-shrink-0 sm:min-w-[130px] w-full sm:w-auto">
                                            <p className="text-xs text-gray-500">Duration</p>
                                            <p className="text-sm font-medium text-gray-700 mt-0.5">Start Date: {campaign.startDate}</p>
                                            <p className="text-sm font-medium text-gray-700">End Date: {campaign.endDate}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            page={campaignPage}
                            totalItems={filteredCampaigns.length}
                            pageSize={PAGE_SIZE}
                            onPageChange={setCampaignPage}
                        />
                    </div>
                )}

                {/* Detail Viewer Modal */}
                {showDetailModal && viewingItem && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDetailModal}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
                            <div className="border-b border-gray-200 px-6 py-4">
                                <h2 className="text-xl font-bold text-gray-800">
                                    {viewingType === 'reward' ? viewingItem.name
                                        : viewingType === 'campaign' ? viewingItem.name
                                            : viewingItem.title}
                                </h2>
                            </div>

                            <div className="px-6 py-6 space-y-4">
                                {viewLoading ? (
                                    <SkeletonBlock className="h-24 w-full" />
                                ) : (
                                    <>
                                        {/* Common description */}
                                        <p className="text-sm text-gray-600 break-words">
                                            {viewingItem.description}
                                        </p>

                                        {viewingItem.mechanics && (
                                            <div className="bg-gray-50 rounded-lg px-4 py-3">
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mechanics</p>
                                                <p className="text-sm text-gray-700 whitespace-pre-line break-words">{viewingItem.mechanics}</p>
                                            </div>
                                        )}

                                        {/* Campaign-specific */}
                                        {viewingType === 'campaign' && viewDetails.heroBanner && (
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Hero Banner</p>
                                                <img
                                                    src={viewDetails.heroBanner.image_url}
                                                    alt={viewDetails.heroBanner.title}
                                                    className="w-full rounded-lg object-cover"
                                                />
                                                <p className="text-sm font-medium text-gray-800 mt-2">{viewDetails.heroBanner.title}</p>
                                                {viewDetails.heroBanner.subtitle && (
                                                    <p className="text-xs text-gray-500">{viewDetails.heroBanner.subtitle}</p>
                                                )}
                                            </div>
                                        )}

                                        {viewingType === 'campaign' && viewingItem.promotionMechanicType && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {viewingItem.promotionMechanicType === 'multiplier' && viewingItem.multiplier !== '' && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Points Multiplier</p>
                                                        <p className="text-sm font-semibold text-gray-800">{viewingItem.multiplier}x</p>
                                                    </div>
                                                )}
                                                {viewingItem.promotionMechanicType === 'discount' && viewingItem.discountValue !== '' && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Discount</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {viewingItem.discountValueType === 'percentage'
                                                                ? `${viewingItem.discountValue}% off`
                                                                : `₱${Number(viewingItem.discountValue).toLocaleString()} off`}
                                                        </p>
                                                    </div>
                                                )}
                                                {viewingItem.minimumSpend !== '' && viewingItem.minimumSpend != null && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Minimum Spend</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            ₱{Number(viewingItem.minimumSpend).toLocaleString()}
                                                        </p>
                                                    </div>
                                                )}
                                                {viewingItem.minOrderCount !== '' && viewingItem.minOrderCount != null && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Min. Orders Required</p>
                                                        <p className="text-sm font-semibold text-gray-800">{viewingItem.minOrderCount}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {(viewingType === 'campaign' || viewingType === 'achievement') && viewDetails.linkedReward && (
                                            <div className="border border-[#8B5A3C] bg-[#F3EAE4] rounded-lg px-4 py-3">
                                                <p className="text-xs font-semibold text-[#8B5A3C] uppercase tracking-wide mb-1">Linked Reward</p>
                                                <p className="text-sm font-medium text-gray-800">{viewDetails.linkedReward.reward_name}</p>
                                                <p className="text-xs text-gray-600 mt-1">{viewDetails.linkedReward.description}</p>
                                                {viewDetails.linkedReward.points_required != null && (
                                                    <p className="text-xs text-gray-500 mt-1">{viewDetails.linkedReward.points_required} pts required</p>
                                                )}
                                            </div>
                                        )}

                                        {viewingType === 'reward' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {viewingItem.rewardLogicType === 'point' && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Points Required</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {viewingItem.pointsRequired ?? '—'} pts
                                                        </p>
                                                    </div>
                                                )}

                                                {viewingItem.rewardValue !== '' && viewingItem.rewardValue != null && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">
                                                            {viewingItem.rewardValueType === 'bonus_points' ? 'Bonus Points Granted' : 'Discount Value'}
                                                        </p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {viewingItem.rewardValueType === 'percentage'
                                                                ? `${viewingItem.rewardValue}% off`
                                                                : viewingItem.rewardValueType === 'bonus_points'
                                                                    ? `${viewingItem.rewardValue} pts`
                                                                    : `₱${Number(viewingItem.rewardValue).toLocaleString()} off`}
                                                        </p>
                                                    </div>
                                                )}

                                                {viewingItem.rewardValueType === 'percentage' && viewingItem.maxDiscountAmount !== '' && viewingItem.maxDiscountAmount != null && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Max Discount Cap</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            ₱{Number(viewingItem.maxDiscountAmount).toLocaleString()}
                                                        </p>
                                                    </div>
                                                )}

                                                {viewingItem.minimumSpend !== '' && viewingItem.minimumSpend != null && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Minimum Spend</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            ₱{Number(viewingItem.minimumSpend).toLocaleString()}
                                                        </p>
                                                    </div>
                                                )}

                                                {viewingItem.rewardLogicType === 'promo' && viewingItem.promoMinOrderCount !== '' && viewingItem.promoMinOrderCount != null && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Min. Orders Required</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {viewingItem.promoMinOrderCount}
                                                        </p>
                                                    </div>
                                                )}

                                                {viewingItem.rewardLogicType === 'order' && viewDetails.orderCountRule && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Min. Orders Required</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {viewDetails.orderCountRule.target_value}
                                                        </p>
                                                    </div>
                                                )}

                                                {viewingItem.rewardLogicType === 'order' && viewDetails.minSpendRule && (
                                                    <div className="border border-gray-200 rounded-lg px-3 py-2">
                                                        <p className="text-xs text-gray-500">Min. Total Spend</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            ₱{Number(viewDetails.minSpendRule.target_value).toLocaleString()}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {viewingItem.rewardLogicType === 'achievement' && viewDetails.requiredAchievement && (
                                            <div className="border border-[#8B5A3C] bg-[#F3EAE4] rounded-lg px-4 py-3">
                                                <p className="text-xs font-semibold text-[#8B5A3C] uppercase tracking-wide mb-1">Required Achievement</p>
                                                <p className="text-sm font-medium text-gray-800">{viewDetails.requiredAchievement.title}</p>
                                                <p className="text-xs text-gray-500 mt-1">{viewDetails.requiredAchievement.reward_points} pts on unlock</p>
                                            </div>
                                        )}

                                        {/* Reward-specific */}
                                        {viewingType === 'reward' && viewDetails.linkedCategory && (
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Applies To Category</p>
                                                <p className="text-sm text-gray-800">{viewDetails.linkedCategory}</p>
                                            </div>
                                        )}

                                        {viewingType === 'reward' && viewDetails.linkedItems?.length > 0 && (
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Linked Menu Item(s)</p>
                                                <div className="space-y-2">
                                                    {viewDetails.linkedItems.map((row, i) => (
                                                        <div key={i} className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2">
                                                            {row.menuItem?.item_img && (
                                                                <img src={row.menuItem.item_img} alt="" className="w-10 h-10 rounded object-cover" />
                                                            )}
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-800">
                                                                    {row.menuItem?.name || 'Item no longer available'}
                                                                </p>
                                                                {row.menuItem?.item_price != null && (
                                                                    <p className="text-xs text-gray-500">
                                                                        ₱{Number(row.menuItem.item_price).toFixed(2)}
                                                                    </p>
                                                                )}
                                                                {row.variant?.variant_name && (
                                                                    <p className="text-xs text-gray-500">
                                                                        Variant: {row.variant.variant_name} (₱{Number(row.variant.price).toFixed(2)})
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {viewingType === 'achievement' && (
                                            <div className="border border-gray-200 rounded-lg px-3 py-2 inline-block">
                                                <p className="text-xs text-gray-500">Reward Points</p>
                                                <p className="text-sm font-semibold text-gray-800">{viewingItem.rewardPoints} pts</p>
                                            </div>
                                        )}

                                        {/* Achievement-specific */}
                                        {viewingType === 'achievement' && viewingItem.condition && (
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Condition</p>
                                                <p className="text-sm text-gray-800">{viewingItem.condition}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
                                <button
                                    onClick={closeDetailModal}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>

    )
}